import React, { memo, useState, useCallback, useMemo, useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';

import { ConnectorScope } from 'types/types';
import { useMeContext } from 'modules/me';
import { AllConnectorsTab } from './AllConnectorsTab';

const useStyles = makeStyles((theme) => ({
  box: {
    background: theme.palette.information.pageBackground,
    position: 'fixed',
    height: '90%'
  },
  tabs: {
    borderBottom: `solid 1px ${theme.palette.grey[300]}`,
    background: theme.palette.common.white
  },
  tab: {
    width: theme.spacing(27)
  },
  indicator: {
    display: 'flex',
    justifyContent: 'center'
  },
  badge: {
    '& .MuiBadge-badge': {
      backgroundColor: theme.palette.error.dark,
      bottom: theme.spacing(1.2),
      left: theme.spacing(-1.2)
    }
  }
}));

export const ConnectorListTabs: React.FC = memo(() => {
  const classes = useStyles();
  const me = useMeContext();
  const location = useLocation();
  const { hash, pathname } = location;

  const isAdmin = useMemo(() => {
    return me?.permissions.hasCompanyConnectorInstallAccess ? true : false;
  }, [me]);

  const idHashValues: any = useMemo(
    () => ({
      '#user': ConnectorScope.User,
      '#company': isAdmin ? ConnectorScope.Tenant : null,
      '#home': ConnectorScope.All
    }),
    [isAdmin]
  );

  const DEFAULT_ID = useMemo(() => ConnectorScope.All, []);

  const setTabIdValue = useCallback(() => {
    if (hash) {
      return idHashValues[hash] || DEFAULT_ID;
    } else {
      return DEFAULT_ID;
    }
  }, [idHashValues, DEFAULT_ID, hash]);

  const [scope, setScope] = useState(setTabIdValue());
  useEffect(() => {
    setScope(setTabIdValue());
  }, [setTabIdValue]);

  return (
    <>
      <Tabs
        value={scope}
        indicatorColor="primary"
        textColor="primary"
        className={classNames(classes.tabs, classes.indicator)}
        TabIndicatorProps={{
          className: classes.indicator,
          children: <span />
        }}
      >
        <Tab
          value={ConnectorScope.All}
          data-testid={'all-connectors-tab'}
          component={Link}
          to={pathname}
          className={classes.tab}
          label={<FormattedMessage id="allConnectors" />}
        />
      </Tabs>
      <Box className={classes.box}>
        <AllConnectorsTab />
      </Box>
    </>
  );
});

ConnectorListTabs.displayName = 'ConnectorListTabs';
